<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card :loading="loading">
          <v-card-title>Setup for {{ pmsConnDetails.usr }}</v-card-title>
          <v-card-subtitle>{{ pmsConnDetails.pmsType }}</v-card-subtitle>
          <div class="text-right">
            <v-btn :loading="loading" @click="linksAddMh1Account" color="primary" class="ma-3">
              <v-icon>mdi-plus</v-icon>MH1 Account
            </v-btn>
            <v-btn :loading="loading" @click="linksAddGBSAccount" color="primary" class="ma-3">
              <v-icon>mdi-plus</v-icon>GBS Account
            </v-btn>
            <v-btn
             :loading="loading"
              @click="linksDiscardChanges"
              :disabled="!linksDirty"
              color="warning"
              class="ma-3"
            >
              <v-icon>mdi-remove</v-icon>Discard Changes
            </v-btn>
            <v-btn
             :loading="loading"
              @click="linksApplyChanges"
              :disabled="!linksDirty"
              color="success"
              class="ma-3"
            >
              <v-icon>mdi-save</v-icon>Apply Changes
            </v-btn>
          </div>
          <span v-if="links">
            <v-card
              v-for="(linkedAccount, accountIndex) in links.accounts"
              :key="accountIndex"
              class="mb-5 mt-5 mx-auto"
              max-width="1000"
            >
              <v-card-text>
                <span v-if="hasMh1PracticeDetails(accountIndex)">
                  <div class="headline mb-1">
                    {{ getMh1PracticeName(linkedAccount.accountId) }}
                  </div>
                  <div>
                    MyHealth1st Practice ID: {{ linkedAccount.accountId }}
                  </div>
                </span>
                <span v-else class="headline mb-1"
                  >{{ linkedAccount.platform }} -
                  {{ linkedAccount.accountId }} -
                  {{ linkedAccount.baseUrl }}</span
                >
              </v-card-text>
              <v-card-actions class="text-right">
                <v-btn
                  color="warning"
                  small
                  @click="unlinkAccount(accountIndex)"
                >
                  <v-icon>mdi-minus</v-icon>Unlink Account
                </v-btn>
              </v-card-actions>
              <v-container class="px-6">
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="linkedAccount.accountId"
                      label="Account ID"
                      placeholder="Mh1 Practice ID or GBS CorpAdminOid"
                      hint="Mh1 Practice ID or GBS CorpAdminOid"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="linkedAccount.timeZone"
                      label="Timezone"
                      placeholder="e.g. Australia/Sydney"
                      hint="Must match timezone defined in OLB platform, e.g Australia/Sydney"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="linkedAccount.notificationChannel"
                      label="Notification Channel"
                      placeholder="Used for real-time PubNub notifications"
                      hint="MH1 Notification Channel or GBS Corpaminoid with environment prefix"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="linkedAccount.baseUrl"
                      label="Base URL"
                      placeholder="e.g. https://test.myhealth1st.com.au"
                      hint="e.g. https://test.myhealth1st.com.au or https://testing.clinicconnect.com.au "
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="linkedAccount.username"
                      label="Sync Username"
                      placeholder="e.g. username of MH1 WS Subscriber"
                      hint="Username of MH1 WS Subscriber or GBS Data Integration"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="linkedAccount.encryptedPassword"
                      label="Sync Password"
                      placeholder="e.g. password of MH1 WS Subscriber"
                      hint="Password of MH1 WS Subscriber or GBS Data Integration"
                      type="password"
                      disabled
                    ></v-text-field>
                    <v-btn @click="setPassword(accountIndex)"
                      >Set Password</v-btn
                    >
                  </v-col>
                </v-row>
              </v-container>
              <div class="px-6">
                <v-switch
                  :input-value="hasOnlineAppointments(accountIndex)"
                  @change="(v) => setOnlineAppointments(accountIndex, v)"
                  :label="`Online Appointments`"
                ></v-switch>
              </div>
              <div class="px-6" v-if="hasOnlineAppointments(accountIndex)">
                <v-simple-table class="px-6" dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th
                          class="text-left"
                          hint="The MH1 Appointment Book ID or GBS OID"
                        >
                          Online Book Id
                        </th>
                        <th class="text-left">PMS Book Id</th>
                        <th class="text-left">Treatment Mappings</th>
                           <th class="text-left">Appointment Type</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(olbBook,
                        olbBookIndex) in linkedAccount.olbBooks"
                        :key="olbBookIndex"
                        valign="top"
                      >
                        <td>
                          <span v-if="hasMh1PracticeDetails(accountIndex)">{{
                            getAppointmentBookName(
                              linkedAccount.platform,
                              linkedAccount.accountId,
                              olbBook.olbBookId
                            )
                          }}</span>
                          <v-text-field
                            v-else
                            hint="The MH1 Appointment Book ID or GBS OID"
                            dense
                            v-model="olbBook.olbBookId"
                          ></v-text-field>
                        </td>
                        <td>
                          <v-text-field
                            hint="The PMS specific Book identifier, e.g. 1::24"
                            dense
                            v-model="olbBook.pmsBookFQId"
                          ></v-text-field>
                        </td>
                        <td>
                          <v-simple-table
                            class="px-0 dense"
                            v-if="olbBook.treatmentMappings"
                          >
                            <template v-slot:default>
                              <tbody>
                                <tr
                                  v-for="(mapping,
                                  mappingIndex) in olbBook.treatmentMappings"
                                  :key="mappingIndex"
                                >
                                  <td>
                                    <span
                                      v-if="
                                        mapping.olbTreatmentId &&
                                        hasMh1PracticeDetails(accountIndex)
                                      "
                                      >{{
                                        getAppointmentBookTreatmentName(
                                          linkedAccount.platform,
                                          linkedAccount.accountId,
                                          olbBook.olbBookId,
                                          mapping.olbTreatmentId
                                        )
                                      }}</span
                                    >
                                    <v-text-field
                                      v-else
                                      dense
                                      v-model="mapping.olbTreatmentId"
                                    ></v-text-field>
                                  </td>
                                  <td></td>
                                  <td>
                                    <v-text-field
                                      dense
                                      v-model="mapping.pmsAppointmentTypeId"
                                    ></v-text-field>
                                  </td>
                                  <td>
                                    <v-btn
                                      color="warning"
                                      x-small
                                      fab
                                      @click="
                                        removeTreatment(olbBook, mappingIndex)
                                      "
                                    >
                                      <v-icon>mdi-minus</v-icon>
                                    </v-btn>
                                  </td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>
                          <div>
                            <v-btn
                              color="primary"
                              x-small
                              @click="addTreatment(olbBook)"
                            >
                              <v-icon>mdi-plus</v-icon>Mapping
                            </v-btn>
                          </div>
                        </td>
                         <td><v-simple-table
                            class="px-0 dense"
                            v-if="olbBook.appointmentTypes"
                          >
                            <template v-slot:default>
                              <tbody>
                                <tr
                                  v-for="(AppointmentType,
                                  appointmentTypeIndex) in olbBook.appointmentTypes"
                                  :key="appointmentTypeIndex"
                                >
                                  <td>
                                    <tr>
                                     {{ getPmsAppointmentTypeName( AppointmentType.name)}}
                                     </tr>
                                  </td>
                                  <td>
                                    <v-btn
                                      color="warning"
                                      x-small
                                      fab
                                      @click="
                                        removeappointmentType(olbBook, appointmentTypeIndex)
                                      "
                                    >
                                      <v-icon>mdi-minus</v-icon>
                                    </v-btn>
                                  </td>
                                </tr>
                              </tbody>
                            </template>
                          </v-simple-table>
                          <div>
                            <!-- <v-btn
                              color="primary"
                              x-small
                              @click="addappointmentType(olbBook)"
                            >
                              <v-icon>mdi-plus</v-icon>Add appointmentType
                            </v-btn> -->
                             <v-row align="baseline">
      
        <v-col cols="5">
            <v-select 
                :items="unmappedAppointmentTypes"
                v-model="newAppointmentType" 
                :item-text="item => item.name"
                item-value="name" 
                label="PMS Appointment Type"
                >
            </v-select>
        </v-col>
        <v-col cols="2">
            <v-btn
            @click="addappointmentType(olbBook,newAppointmentType)"
            color="primary"
            :disabled="!canAddAppointmentType"
            small icon
            ><v-icon color="primary ">mdi-plus-circle</v-icon></v-btn>
        </v-col>
      </v-row>
                          </div>
                              </td>
                        <td>
                          <v-btn
                            color="warning"
                            small
                            @click="removeBook(linkedAccount, olbBookIndex)"
                          >
                            <v-icon>mdi-minus</v-icon>Book
                          </v-btn>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
                <v-btn class="primary" small @click="addBook(linkedAccount)">
                  <v-icon>mdi-plus</v-icon>Book
                </v-btn>
              </div>
              <div
                class="px-6"
                v-if="linkedAccount.platform.toLowerCase() == 'mh1'"
              >
                <v-switch
                  :input-value="hasMh1EasyRecalls(accountIndex)"
                  @change="(v) => setMh1EasyRecalls(accountIndex, v)"
                  :label="`MH1 Easy Recalls`"
                ></v-switch>
              </div>
              <div class="px-6" v-if="hasMh1EasyRecalls(accountIndex)">
                <v-text-field
                  v-model="linkedAccount.mh1EasyRecalls.pmsSiteId"
                  label="PMS Site Id"
                  placeholder="The PMS Site ID to be used for EasyRecalls with this account"
                ></v-text-field>
              </div>
            </v-card>
          </span>
          <span v-else-if="loading">
          </span>
          <span v-else>No Links information avilable</span>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>


<script>
import { API, graphqlOperation } from "aws-amplify";
import * as queries from "@/graphql/queries";
import * as mutations from "@/graphql/mutations";

export default {
  props: {
    pmsConnDetails: Object,
  },
  created: async function () {
    this.sub = this.$route.params.sub;
    
    this.getLinks(this.sub);
    this.getPMSInfo(this.sub);
  },
  data: function () {
    return {
      sub: "",
      pmsInfo : Object,
      links: null,
      linksUnchanged: null,
      date: new Date().toISOString().substr(0, 10),
      linksDirty: false,
      linksStateReset: false,
      mh1PracticeDetails: {},
      loading: false,
       newAppointmentType:Object,
    };
  },
  watch: {
    links: {
      handler(newVal, oldVal) {
        console.log("watch Links handler", newVal, oldVal);
        if (this.linksStateReset) {
          console.log(
            "watch links handler ignoring change, linksStateReset true"
          );
          this.linksStateReset = false;
          return;
        }

        this.linksDirty = true;
        console.log("watch Links handler set dirty");
      },
      deep: true,
    },
  },
  async beforeRouteLeave(to, from, next) {
    console.log("beforeRouteLeave");
    if (!this.linksDirty) {
      next();
    } else {
      const res = await this.$dialog.warning({
        text:
          "Your pending changes have not been saved. Are you sure you want to leave?",
        title: "Warning",
        icon: "mdi-alert",
      });

      if (res) {
        next();
      } else {
        next(false);
      }
    }
  },
 
  computed: {
       canAddAppointmentType: function() {
      return (this.newAppointmentType);
    },
       unmappedAppointmentTypes() {
         return this.pmsInfo.appointmentTypes;
    },
  },
   methods: {
    
     async getPMSInfo(sub) {
      this.loading = true;
      try {
        // Simple query
        //const allStatus = await API.graphql(graphqlOperation(queries.listStatus));
        console.log({ msg: "getPMSInfo", sub });
        const pmsInfo = await API.graphql(
          graphqlOperation(queries.getPmsInfo, { sub: sub })
        );

        console.log("pmsInfo", pmsInfo);

        if ((pmsInfo?.data?.getPMSInfo?.usr ?? "") === "") {
          this.msg = "Can't retrieve PMS Info details";
          return;
        }

        const jsonObj = JSON.parse(
          JSON.parse(pmsInfo.data.getPMSInfo?.details)
        );

        //this.msg = status.data.getPMSInfo.usr;
        this.pmsInfo = jsonObj;

        console.log("parse pmsinfo", this.pmsInfo);
      } catch (err) {
        this.msg = err;
        this.items = {};
        console.log(err);
      } finally {
        this.loading = false;
      }
    },
    async getLinks(sub) {
      try {
        this.loading = true;
        // Simple query
        //const allStatus = await API.graphql(graphqlOperation(queries.listStatus));
        console.log("getLinks called", sub);
        const status = await API.graphql(
          graphqlOperation(queries.getLinks, { sub: sub })
        );

        console.log("getLinks returned", status);

        if (!status.data?.getLinks?.sub) {
          console.log("Can't retrieve Links details");
        }

        const result = status.data?.getLinks || {
          sub: sub,
          accCount: 0,
          ctd: new Date().toString(),
          ctdTs: +new Date(),
          accounts: [],
        };

        const mh1Ids = result.accounts
          .filter((acc) => acc.platform.toLowerCase() == "mh1" && acc.accountId)
          .map((acc) => acc.accountId);

        console.log("found mh1 accounts", mh1Ids, result.accounts);

        for (const mh1Id of mh1Ids) {
          try {
            const mh1Details = await API.graphql(
              graphqlOperation(queries.getMh1PracticeDetails, {
                practiceId: Number(mh1Id),
              })
            );

            const mh1AppBookDetails = await API.graphql(
              graphqlOperation(queries.getMh1PracticeAppointmentBooks, {
                practiceId: Number(mh1Id),
              })
            );

            const mh1Treatments = await API.graphql(
              graphqlOperation(queries.getMh1PracticeTreatments, {
                practiceId: Number(mh1Id),
              })
            );

            const detailsToPersist = mh1Details.data.getMh1PracticeDetails;

            detailsToPersist.appointmentBooks =
              mh1AppBookDetails.data.getMh1PracticeAppointmentBooks;

            detailsToPersist.treatments =
              mh1Treatments.data.getMh1PracticeTreatments;

            console.log("got mh1 details", mh1Id, detailsToPersist);
            this.mh1PracticeDetails[mh1Id.toString()] = detailsToPersist;
          } catch (err) {
            console.log("error getting mh1 details", mh1Id, err);
          }
        }

        this.linksUnchanged = JSON.parse(JSON.stringify(result));
        this.linksStateReset = true;
        this.links = result;
        this.linksDirty = false;
      } catch (err) {
        this.msg = err;
        this.items = {};
        console.log(err);
      } finally {
        this.loading = false;
      }
    },
    async handleResponseError(operationDescription, response) {
      console.log("handleResponseError", response);
      const notifyResult = await this.$dialog.notify.error(
        operationDescription,
        {
          position: "top-right",
          timeout: 5000,
          actions: [{ text: "Info", "x-small": true, color: "secondary" }],
        }
      );

      if (notifyResult == "Info") {
        this.$dialog.error({
          text: JSON.stringify(response?.errors || response),
          title: operationDescription,
          icon: "mdi-error",
        });
      }
    },
    linksAddMh1Account() {
      this.links.accounts.push({
        platform: "mh1",
        accountId: "",
        baseUrl: "https://test.myhealth1st.com.au",
        products: [],
        olbBooks: [],
        mh1EasyRecalls: {},
      });

      this.links.accCount = this.links?.accounts?.length || 1;
    },
    linksAddGBSAccount() {
      this.links.accounts.push({
        platform: "gbs",
        accountId: "",
        baseUrl: "https://testing.clinicconnect.com.au",
        products: [],
        olbBooks: [],
        mh1EasyRecalls: {},
      });

      this.links.accCount = this.links?.accounts?.length || 1;
    },
    hasMh1PracticeDetails(accountIndex) {
      const acc = this.links.accounts[accountIndex];

      if (
        acc.platform.toLowerCase() == "mh1" &&
        acc.accountId in this.mh1PracticeDetails
      ) {
        return true;
      } else {
        return false;
      }
    },
    getMh1PracticeName(accountId) {
      return this.mh1PracticeDetails[accountId].name;
    },
    getAppointmentBookName(platform, accountId, olbBookId) {
      if (platform.toLowerCase() != "mh1") {
        return olbBookId;
      }
      if (!accountId in this.mh1PracticeDetails) {
        return olbBookId;
      }
      const thisBook = this.mh1PracticeDetails[accountId].appointmentBooks.find(
        (olbBook) => olbBook.id == olbBookId
      );
      if (!thisBook) {
        console.log(
          "getAppointmentBookName cant find matching book",
          olbBookId,
          this.mh1PracticeDetails[accountId].appointmentBooks
        );
        return olbBookId;
      }

      return `${thisBook.subscriber.name} ${thisBook.subscriber.surname} - ${olbBookId}`;
    },
    getAppointmentBookTreatmentName(
      platform,
      accountId,
      olbBookId,
      olbTreatmentId
    ) {
      if (platform.toLowerCase() != "mh1") {
        return olbTreatmentId;
      }
      if (!accountId in this.mh1PracticeDetails) {
        return olbTreatmentId;
      }
      const thisBook = this.mh1PracticeDetails[
        accountId
      ].treatments.appointmentBooks.find(
        (olbBook) => olbBook.appointmentBookId == olbBookId
      );
      if (!thisBook) {
        console.log(
          "getAppointmentBookTreatmentName cant find matching treatment book",
          olbBookId,
          olbTreatmentId,
          this.mh1PracticeDetails[accountId].treatments
        );
        return olbTreatmentId;
      }

      const thisTreatment = thisBook.treatments.find(
        (t) => t.treatmentId == olbTreatmentId
      );

      if (!thisTreatment) {
        console.log(
          "getAppointmentBookTreatmentName cant find matching treatment in book",
          olbBookId,
          olbTreatmentId,
          thisBook
        );
        return olbTreatmentId;
      }

      return `${thisTreatment.name} (${thisTreatment.duration} mins)`;
    },
    formatSeconds(secs) {
      //return secs;
      const date = new Date(null);
      date.setSeconds(secs); // specify value for SECONDS here
      return date.toISOString().substr(11, 8);
    },
    getProductDescription(prod) {
      let res = "";
      switch (prod) {
        case "olb":
          res = "Online Appointments";
          break;
        default:
          res = prod;
          break;
      }
      return res;
    },
    hasProduct(productName, index) {
      const products = this.links.accounts[index].products;
      if (!products) {
        return false;
      }

      const r = products.some((prod) => prod === productName);
      return r;
    },
    setProduct(productName, index, value) {
      console.log(`setProduct:`, productName, index, value);
      const products = this.links.accounts[index].products;
      console.log(`setProduct:`, productName, index, products, value);
      if (!products) {
        return false;
      }
      const pi = products.indexOf(productName);
      if (value && pi < 0) {
        products.push(productName);
        console.log(`setProduct pushed olb`, productName, products, pi);
      } else if (!value && pi >= 0) {
        products.splice(pi, 1);
        console.log(`setProduct removed olb`, productName, products, pi);
      } else {
        console.log(`setProduct no action`, productName, products, pi);
      }
    },
    hasOnlineAppointments(index) {
      return this.hasProduct("olb", index);
    },
    setOnlineAppointments(index, value) {
      const r = this.setProduct("olb", index, value);
      return r;
    },
    hasMh1EasyRecalls(index) {
      return this.hasProduct("mh1EasyRecalls", index);
    },
    setMh1EasyRecalls(index, value) {
      const r = this.setProduct("mh1EasyRecalls", index, value);
      if (!this.links.accounts[index]?.mh1EasyRecalls) {
        this.links.accounts[index].mh1EasyRecalls = { pmsSiteId: "" };
      }
    },
    removeTreatment(olbBook, mappingIndex) {
      console.log("removeTreatment", olbBook, mappingIndex);
      olbBook.treatmentMappings.splice(mappingIndex, 1);
    },
    removeappointmentType(olbBook, appointmentTypeIndex) {
      console.log("removeappointmentType", olbBook, appointmentTypeIndex);
      olbBook.appointmentTypes.splice(appointmentTypeIndex, 1);
    },
    
    addTreatment(olbBook) {
      console.log("addTreatment", olbBook);
      olbBook.treatmentMappings = olbBook.treatmentMappings || [];
      olbBook.treatmentMappings.push({
        pmsAppointmentTypeId: "",
        olbTreatmentId: "",
      });
    },
     addappointmentType(olbBook,newappointmentType) {
      console.log("addappointmentType", olbBook);
      olbBook.appointmentTypes = olbBook.appointmentTypes || [];
      olbBook.appointmentTypes.push({
         name: newappointmentType,
      });
    },
    removeBook(linkedAccount, olbBookIndex) {
      console.log("removeBook", linkedAccount, olbBookIndex);
      linkedAccount.olbBooks.splice(olbBookIndex, 1);
    },
    addBook(linkedAccount) {
      console.log("addBook", linkedAccount);
      linkedAccount.olbBooks = linkedAccount.olbBooks || [];
      linkedAccount.olbBooks.push({
        olbBookId: "",
        pmsBookFQId: "",
        treatmentMappings: [],
      });
    },
    async linksDiscardChanges() {
      console.log("linksDiscardChanges", this.links, this.linksUnchanged);
      const res = await this.$dialog.confirm({
        text: "Do you really want to discard your changes?",
        title: "Warning",
      });

      if (!res) {
        console.log("linksDiscardChanges, cancelled");
        return;
      }

      this.links = JSON.parse(JSON.stringify(this.linksUnchanged));
      this.linksStateReset = true;
      this.linksDirty = false;
    },
    async linksApplyChanges() {
      console.log("linksApplyChanges", this.links);

      try {
        const status = await API.graphql(
          graphqlOperation(mutations.updateLinks, { input: this.links })
        );

        this.$dialog.notify.info("Links updated");

        this.linksUnchanged = status.data.updateLinks;
        this.linksStateReset = true;
        this.links = JSON.parse(JSON.stringify(this.linksUnchanged));
        this.linksDirty = false;

        console.log("linksApplyChanges result", status);
      } catch (err) {
        console.log("linksApplyChanges error", err);

        const notifyResult = await this.$dialog.notify.error(
          "Error when saving links.",
          {
            position: "top-right",
            timeout: 5000,
            actions: [{ text: "Info", "x-small": true, color: "secondary" }],
          }
        );

        console.log(
          "linksApplyChanges notifyResult",
          notifyResult,
          err,
          JSON.stringify(err)
        );
        if (notifyResult == "Info") {
          this.$dialog.error({
            text: JSON.stringify(err),
            title: "Cannot save links",
            icon: "mdi-error",
          });
        }
      }
    },
    async unlinkAccount(accountIndex) {
      console.log("unlinkAccount", accountIndex);
      const acc = this.links.accounts[accountIndex];
      if (!acc) {
        return;
      }

      const res = await this.$dialog.confirm({
        text: "Are you sure you want to unlink this account?",
        title: "Warning",
      });

      if (!res) {
        console.log("unlinkAccount, cancelled");
        return;
      }

      this.links.accounts.splice(accountIndex, 1);
      this.links.accCount = this.links?.accounts?.length || 0;
    },
    bytesToSize(bytes) {
      var sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      if (bytes == 0) return "0 Byte";
      var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
      return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
    },
    async setPassword(accountIndex) {
      try {
        console.log("setPassword", accountIndex);
        const acc = this.links.accounts[accountIndex];

        const res = await this.$dialog.prompt({
          title: "Account Password",
          text: "Please enter the account password",
        });

        console.log("setPassword prompt response", res);

        if (!res) {
          return;
        }

        const encResponse = await API.graphql(
          graphqlOperation(queries.encryptValue, { input: res })
        );

        console.log("setPassword got response", encResponse);

        const r = encResponse?.data?.encryptValue?.data;

        if (!r) {
          const notifyResult = await this.$dialog.notify.error(
            "Error setting password",
            {
              position: "top-right",
              timeout: 5000,
              actions: [{ text: "Info", "x-small": true, color: "secondary" }],
            }
          );

          if (notifyResult == "Info") {
            this.$dialog.error({
              text:
                encResponse?.data?.encryptValue?.error ??
                JSON.stringify(encResponse),
              title: "Cannot set password",
              icon: "mdi-error",
            });
          }
          return;
        }

        console.log("setPassword parsed response", r);

        acc.encryptedPassword = r;
      } catch (err) {
        const notifyResult = await this.$dialog.notify.error(
          "Error setting password",
          {
            position: "top-right",
            timeout: 5000,
            actions: [{ text: "Info", "x-small": true, color: "secondary" }],
          }
        );

        if (notifyResult == "Info") {
          this.$dialog.error({
            text: JSON.stringify(err),
            title: "Cannot set password",
            icon: "mdi-error",
          });
        }
      }
    },
      getPmsAppointmentTypeName: function(name) {
      if (!this.pmsInfo) {
        return "n/a";
      }
      const t = this.pmsInfo.appointmentTypes.find(t => t.name == name);

      if (!t) {
        return `${this.pmsInfo.pmsAppointmentTypeId}`
      }

      return t.name ;
    },
   
  },
};
</script>